import React from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';
import { useToast } from './ToastContext';
import { useTranslation } from 'react-i18next';
import UserProfileModal from '../components/UserProfileModal';
import { useMemoryCleanup, useComponentLifecycle } from '../hooks/usePerformanceOptimization';

const EventContext = React.createContext();
const SocketContext = React.createContext(null);
export const useEvents = () => React.useContext(EventContext);
export const useEventSocket = () => React.useContext(SocketContext);

// Helper für bedingtes Logging
const debugLog = (...args) => {
    if (process.env.NODE_ENV !== 'production') {
        console.log(...args);
    }
};

export const EventProvider = ({ children }) => {
    const { isAuthenticated, user, api, getToken, ensureCsrfToken } = useAuth();
    const { showError } = useToast();
    const { showNotification } = useNotification();
    const { t } = useTranslation();
    const [events, setEvents] = React.useState([]);
    const [error, setError] = React.useState(null);
    const socketRef = React.useRef(null);
    // pending user who requested to join (for creator to preview selfie)
    const [pendingRequester, setPendingRequester] = React.useState(null);
    // NEU: Wir speichern die aktuellen Kartengrenzen hier im Context
    const mapBoundsRef = React.useRef(null);
    // Ref für den Debounce-Timer (verhindert API-Spam)
    const fetchTimeoutRef = React.useRef(null);

    // Performance Optimization Hooks
    const { addTimer, addInterval, addListener } = useMemoryCleanup();
    useComponentLifecycle('EventProvider');

    // API Cache für Performance
    const apiCacheRef = React.useRef(new Map());
    const lastFetchRef = React.useRef(null);

    // Berechne die Anzahl der ausstehenden Anfragen für Events, die der User erstellt hat
    const pendingRequestCount = React.useMemo(() => {
        if (!user) return 0;
        return events.reduce((count, event) => {
            if (event.user_id === user.id && event.participants) {
                const pendingCount = event.participants.filter(p => p.status === 'pending').length;
                return count + pendingCount;
            }
            return count;
        }, 0);
    }, [events, user]);

    // Diese Funktion wird jetzt von der MapPage aufgerufen, um die Grenzen zu speichern
    const setMapBounds = React.useCallback((bounds) => {
        mapBoundsRef.current = bounds;
    }, []);

    // Diese Funktion lädt Events für die aktuell gespeicherten Grenzen mit Debouncing & Caching
    const refetchEventsForCurrentView = React.useCallback(() => {
        if (!isAuthenticated) return;
        
        // Performance: Abbrechen wenn Tab im Hintergrund ist (spart Serverlast bei 10k Usern)
        if (document.hidden) return;

        const bounds = mapBoundsRef.current;
        if (bounds) {
            // Cache Key für Bounds
            const cacheKey = `${bounds.getSouth()}_${bounds.getNorth()}_${bounds.getWest()}_${bounds.getEast()}`;
            
            // Check Cache (30 Sekunden gültig)
            const cached = apiCacheRef.current.get(cacheKey);
            const now = Date.now();
            if (cached && (now - cached.timestamp) < 30000) {
                debugLog('🔍 EventContext - Using cached events for bounds:', bounds);
                setEvents(cached.events);
                return;
            }

            debugLog('🔍 EventContext - API Call mit Bounds:', bounds);
            api.get(`/api/events?minLat=${bounds.getSouth()}&maxLat=${bounds.getNorth()}&minLon=${bounds.getWest()}&maxLon=${bounds.getEast()}`)
               .then(res => {
                   setEvents(res.data);
                   setError(null); // Reset error on success
                   
                   // Cache Ergebnis
                   apiCacheRef.current.set(cacheKey, {
                       events: res.data,
                       timestamp: now
                   });
               })
                .catch(err => {
                    console.error('API Error:', err);
                    showError(t('error_fetching_events', 'Could not refresh the vibes on the map.'));
                });
        } else {
            debugLog('EventContext - No map bounds - using geolocation');
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const geoKey = `geo_${pos.coords.latitude}_${pos.coords.longitude}`;
                    const geoCached = apiCacheRef.current.get(geoKey);
                    
                    if (geoCached && (now - geoCached.timestamp) < 30000) {
                        setEvents(geoCached.events);
                        return;
                    }
                    
                    api.get(`/api/events?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`)
                       .then(res => {
                           setEvents(res.data);
                           apiCacheRef.current.set(geoKey, {
                               events: res.data,
                               timestamp: now
                           });
                       })
                        .catch(err => showError(t('error_fetching_events', 'Could not refresh the vibes on the map.')));
                },
                (err) => {
                    console.warn('Geolocation denied - using default location (Berlin)');
                    // Fallback auf Berlin um "Fetch All" zu verhindern
                    const berlinKey = 'geo_52.5200_13.4050';
                    const berlinCached = apiCacheRef.current.get(berlinKey);
                    
                    if (berlinCached && (now - berlinCached.timestamp) < 30000) {
                        setEvents(berlinCached.events);
                        return;
                    }
                    
                    api.get(`/api/events?lat=52.5200&lon=13.4050`)
                       .then(res => {
                           setEvents(res.data);
                           apiCacheRef.current.set(berlinKey, {
                               events: res.data,
                               timestamp: now
                           });
                       })
                        .catch(err => {
                            showError(t('error_fetching_events_default', 'Could not load default events.'));
                        });
                },
                { timeout: 5000, maximumAge: 60000 } // Cache Geolocation für 1 Minute
            );
        }
    }, [isAuthenticated, api, showError, t]);

    React.useEffect(() => {
        if (isAuthenticated) {
            refetchEventsForCurrentView();

            if (socketRef.current) socketRef.current.disconnect();
            
            const socketUrl = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_API_URL : 'http://localhost:3002';
            const token = getToken();
            
            // Socket Optimierung: Transports explizit setzen um Polling-Overhead zu vermeiden
            socketRef.current = io(socketUrl, {
                auth: { token },
                transports: ['websocket'],
                reconnection: true,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                reconnectionAttempts: 10,
                forceNew: false,
                upgrade: false
            });
            const socket = socketRef.current;

            // Socket Event Listener mit Memory Cleanup
            const handleConnect = () => debugLog('[Socket] Connected:', socket.id);
            const handleDisconnect = (reason) => {
                console.warn('[Socket] Disconnected:', reason);
                if (reason === 'io server disconnect') {
                    addTimer(() => socket.connect(), 1000);
                }
            };
            const handleNewEvent = (newEvent) => {
                debugLog('[Socket] Received newEvent', newEvent);
                setEvents(prevEvents => [newEvent, ...prevEvents.filter(e => e.id !== newEvent.id)]);
            };
            const handleEventUpdated = (updatedEvent) => {
                debugLog('[Socket] Received eventUpdated', updatedEvent);
                setEvents(prevEvents =>
                    prevEvents.map(event =>
                        event.id === updatedEvent.id ? updatedEvent : event
                    )
                );
                // If the updated event wasn't in the list, add it.
                setEvents(prevEvents => {
                    if (!prevEvents.some(e => e.id === updatedEvent.id)) {
                        return [updatedEvent, ...prevEvents];
                    }
                    return prevEvents;
                });
            };

            // Event Listener mit Performance Hook registrieren
            addListener(socket, 'connect', handleConnect);
            addListener(socket, 'disconnect', handleDisconnect);
            addListener(socket, 'newEvent', handleNewEvent);
            addListener(socket, 'eventUpdated', handleEventUpdated);
            socket.on('eventDeleted', ({ eventId }) => {
                debugLog('[Socket] Received eventDeleted', eventId);
                setEvents(prevEvents => prevEvents.filter(event => event.id !== eventId));
            });

            // Notifications
            // render modal for pending requester if exists
            socket.on('joinRequestReceived', (data) => {
                if (user && data.creatorId === user.id) {
                    showNotification(t('join_request_notification', { name: data.requesterName, event: data.eventTitle }) || `${data.requesterName} wants to join "${data.eventTitle}"`, data.eventId);
                    // show requester selfie for 2 seconds
                    if (data.requesterSelfie) {
                        setPendingRequester({ nickname: data.requesterName, selfie_path: data.requesterSelfie });
                        setTimeout(() => setPendingRequester(null), 2000);
                    }
                }
            });

            socket.on('requestStatusChanged', (data) => {
                if (user && data.participantId === user.id) {
                    // Refetch um Status-Update in UI sofort zu sehen
                    refetchEventsForCurrentView(); 
                    if (data.status === 'accepted') {
                        showNotification(t('request_accepted_notification', { event: data.eventTitle }), data.eventId);
                    } else {
                        showNotification(t('request_rejected_notification', { event: data.eventTitle }), data.eventId);
                    }
                }
            });

            socket.on('eventCanceled', (data) => {
                if (user && data.participantId === user.id) {
                    refetchEventsForCurrentView();
                    showNotification(t('event_canceled_notification', { event: data.eventTitle }));
                }
            });

            socket.on('participantLeft', (data) => {
                if (user && data.creatorId === user.id) {
                    refetchEventsForCurrentView(); // Update participant count
                    showNotification(t('participant_left_notification', { name: data.leaverName, event: data.eventTitle }), data.eventId);
                }
            });

            return () => {
                socket.off(); // Entfernt alle Listener auf einmal
                socket.disconnect();
            };
        }
    }, [isAuthenticated, user, showNotification, t, getToken, refetchEventsForCurrentView]);

    // Debounce Logic für Map-Bewegungen
    const fetchEventsByBounds = React.useCallback((bounds) => {
        if (!isAuthenticated) return;
        setMapBounds(bounds);
        
        if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);

        fetchTimeoutRef.current = setTimeout(() => {
            api.get(`/api/events?minLat=${bounds.getSouth()}&maxLat=${bounds.getNorth()}&minLon=${bounds.getWest()}&maxLon=${bounds.getEast()}`)
               .then(res => setEvents(res.data))
                .catch(err => {
                    console.error('Fetch error', err);
                    showError(t('error_fetching_events', 'Could not refresh the vibes on the map.'));
                });
        }, 500);
    }, [isAuthenticated, setMapBounds, api, showError, t]);

    const createEvent = async (eventData) => {
        await ensureCsrfToken();
        const response = await api.post('/api/events/create', eventData);
        return response;
    };

    const joinEvent = async (eventId) => {
        await ensureCsrfToken();
        return api.post(`/api/events/${eventId}/join`);
    };
    
    const updateParticipantStatus = async (eventId, participantId, status) => {
        const res = await api.put(`/api/events/${eventId}/participant`, { participant_id: participantId, status });
        // Optimistisches Update oder Refetch? Refetch ist sicherer für Konsistenz
        refetchEventsForCurrentView();
        return res;
    };

    const deleteEvent = async (eventId) => {
        await ensureCsrfToken();
        // BUG-FIX: Der API-Pfad war falsch, es fehlte das "/api"-Präfix.
        // Hinzugefügt: Bessere Fehlerbehandlung mit Toast-Notification.
        return api.delete(`/api/events/${eventId}`).catch(err => {
            console.error('Delete event failed:', err);
            // Wirf den Fehler weiter, damit die aufrufende Komponente reagieren kann,
            // und zeige dem User eine verständliche Nachricht.
            throw new Error(t('error_deleting_event', 'Could not delete the event.'));
        });
    };
    const leaveEvent = async (eventId) => {
        await ensureCsrfToken();
        return api.delete(`/api/events/${eventId}/leave`);
    };
    
    const value = React.useMemo(() => ({
        events, error, createEvent, joinEvent, updateParticipantStatus, deleteEvent, leaveEvent, fetchEventsByBounds, setMapBounds, pendingRequestCount
    }), [events, error, fetchEventsByBounds, setMapBounds, pendingRequestCount, ensureCsrfToken]);
    
    return (
        <SocketContext.Provider value={socketRef.current}>
            <EventContext.Provider value={value}>
                {children}
                {pendingRequester && (
                    <UserProfileModal user={pendingRequester} onClose={() => setPendingRequester(null)} />
                )}
            </EventContext.Provider>
        </SocketContext.Provider>
    );
};
